import { memoizedGroupBySelector } from '@travelnest/entities';
import { Loader } from '@travelnest/merlin.loader';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { IPropertySummary } from '../../../data/entities/property-summary';
import { ISessionState } from '../../../entities/auth/session/reducers';
import { useCustomerExperiments } from '../../../experiment';
import { logging } from '../../../logging';
import { IState } from '../../../store/reducer';
import { hasOnlyOneUnpublishedProperty } from '../../../view/helpers/customer';
import { useOnboardingComplete } from '../../../view/helpers/hooks';
import { HelpFeaturedContainer } from './components';
import { LOGIN_ROUTES } from './constants';

export const getNextUrl = (redirectUrl?: string) => {
	let nextUrl = '/';
	if (redirectUrl) {
		try {
			nextUrl = decodeURIComponent(redirectUrl);
		} catch (e) {
			logging.error(
				'Malformed redirect URL on login completion',
				redirectUrl
			);
		}
	}
	return nextUrl;
};

export const LoginComplete: React.FC<{ currentSession: ISessionState }> = (
	props
) => {
	const navigate = useNavigate();
	const { isValid, ownerId } = props.currentSession;

	if (!isValid) {
		React.useEffect(() => {
			logging.error(`Routed to LoginComplete without valid session`);
			navigate(LOGIN_ROUTES.ERROR);
		}, []);
		return null;
	}

	const redirectUrl = useSelector(
		(state: IState) => state.entities.redirect.url
	);

	const customerExperiments = useCustomerExperiments(ownerId);
	const [loading, onboardingComplete, propertyId] =
		useOnboardingComplete(ownerId);
	const properties = useSelector<IState, IPropertySummary[]>(
		(state) =>
			memoizedGroupBySelector(
				'owner_id',
				state.data?.propertySummaries?.byId
			)[ownerId] || []
	);

	React.useEffect(() => {
		if (loading) return;
		let nextUrl = getNextUrl(redirectUrl);

		// Task-Based Property and Property Portal track onboarding differently
		const propertyIdToRedirectTo =
			customerExperiments?.property_portal === 'on'
				? hasOnlyOneUnpublishedProperty(properties) && properties[0].id
				: !onboardingComplete && propertyId;
		if (propertyIdToRedirectTo) {
			nextUrl = `/property/${propertyIdToRedirectTo}/onboard`;
		}

		navigate(nextUrl, { replace: true });
	}, [redirectUrl, properties, onboardingComplete, loading]);

	return (
		<HelpFeaturedContainer>
			<div className="loader" style={{ height: '30px' }}>
				<Loader loading={loading} />
			</div>
		</HelpFeaturedContainer>
	);
};
